import { useEffect, useRef, useState } from 'react'
import { SubmitErrorHandler, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'
import {
  PolicyPeriodDuration,
  PolicyPeriodDurationLabel,
  RolloverOptions
} from 'Utils/types/benefitProgramPolicy'
import { CurrencySymbol, getCurrency } from 'Utils/types/currencies'
import { BenefitProgramCategory } from 'Utils/types/benefitProgramCategory'
import { useCreateBenefitProgramQuery } from 'Hooks/api/JOONAdmin/useCreateBenefitProgramQuery'
import { CountryCode } from 'Utils/types/countries'
import { isAmount, parseAmount } from 'Utils/helpers/currency'
import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { EmployerBenefitProgram } from 'Utils/types/benefitProgram'
import { useUpdateBenefitProgramQuery } from 'Hooks/api/JOONAdmin/useUpdateBenefitProgramQuery'
import { useUpdateBenefitProgramPolicyQuery } from 'Hooks/api/JOONAdmin/useUpdateBenefitProgramPolicyQuery'
import { useEndBenefitProgramPolicyQuery } from 'Hooks/api/JOONAdmin/useEndBenefitProgramPolicyQuery'
import { useCountries } from 'Hooks/useCountries'
import isSameDay from 'date-fns/isSameDay'
import { getAnnualResetMonthOptions } from 'Utils/helpers/dateSelection'
import { CategoryTaxability, convertToBackendFormat } from './CategoryTaxabilitySelector'

export type BenefitProgramFormValues = {
  name: string
  scheduledStartDate: Date
  scheduledEndDate?: Date
  allowance: string
  policyPeriod: PolicyPeriodDuration
  rollover: RolloverOptions
  ongoingPurchases: boolean
  ongoingPurchaseThreshold?: number
  countries: CountryCode[]
  benefitProgramLink: string
  selectedCategories: BenefitProgramCategory['id'][]
  categoryTaxableSettings: Record<number, CategoryTaxability>
  deleteFuturePolicies?: boolean
  eligibilityDurationDays?: string
}

export enum OneTimeDuration {
  Indefinite = 'indefinite',
  Days = 'days'
}

const DEFAULT_THRESHOLD = 1.01
export const useBenefitProgramForm = (
  closeModal: () => void,
  benefitProgram?: EmployerBenefitProgram
) => {
  const { countryOptions } = useCountries()

  const defaultValues: Partial<BenefitProgramFormValues> | undefined = benefitProgram
    ? {
        name: benefitProgram.name,
        scheduledStartDate: benefitProgram.policy.scheduledStartDate,
        scheduledEndDate: benefitProgram.policy.scheduledEndDate,
        allowance: benefitProgram.policy.expenditureAllowance.toString(),
        policyPeriod: benefitProgram.policy.policyPeriodDuration,
        rollover: benefitProgram.policy.rollover,
        ongoingPurchases: benefitProgram.policy.ongoingPurchases,
        ongoingPurchaseThreshold:
          benefitProgram.policy.ongoingPurchaseThreshold ?? DEFAULT_THRESHOLD,
        countries: benefitProgram.countries,
        benefitProgramLink: benefitProgram.programDescriptionLink,
        selectedCategories: benefitProgram.categories.map((cat) => cat.id),
        categoryTaxableSettings: benefitProgram.categories.reduce(
          (acc, cat) => {
            acc[cat.id] = cat.taxable
            return acc
          },
          {} as Record<number, CategoryTaxability>
        ),
        deleteFuturePolicies: false
      }
    : {
        rollover: RolloverOptions.Disabled,
        ongoingPurchaseThreshold: DEFAULT_THRESHOLD,
        countries: [countryOptions[0].value],
        categoryTaxableSettings: {}
      }

  const {
    register,
    control,
    handleSubmit,
    setValue,
    resetField,
    formState: { errors }
  } = useForm<BenefitProgramFormValues>({ defaultValues })

  const {
    state: { employerId }
  } = useEmployerInfoContext()

  const { mutate: createBenefitProgramQuery } = useCreateBenefitProgramQuery()
  const { mutate: updateBenefitProgramQuery } = useUpdateBenefitProgramQuery()
  const { mutate: updateBenefitProgramPolicyQuery } = useUpdateBenefitProgramPolicyQuery()
  const { mutate: endBenefitProgramPolicyQuery } = useEndBenefitProgramPolicyQuery()

  const [programCadence, setProgramCadence] = useState(
    benefitProgram ? benefitProgram.policy.policyPeriodDuration : PolicyPeriodDuration.Monthly
  )

  const [startDateChanged, setStartDateChanged] = useState(false)

  const [annualResetMonth, setAnnualResetMonth] = useState<string | undefined>(undefined)
  const [oneTimeDuration, setOneTimeDuration] = useState(OneTimeDuration.Indefinite)

  const scheduledStartDate = useWatch({ control, name: 'scheduledStartDate' })

  const dateBasis = `${programCadence}-${annualResetMonth ?? ''}`
  const endDateBasis = `${dateBasis}-${scheduledStartDate?.getTime() ?? ''}`
  const previousDateBasis = useRef(dateBasis)
  const previousEndDateBasis = useRef(endDateBasis)

  useEffect(() => {
    if (previousDateBasis.current === dateBasis) return

    previousDateBasis.current = dateBasis
    resetField('scheduledStartDate')
  }, [dateBasis, resetField])

  useEffect(() => {
    if (previousEndDateBasis.current === endDateBasis) return

    previousEndDateBasis.current = endDateBasis
    resetField('scheduledEndDate')
  }, [endDateBasis, resetField])

  const isAnnual = programCadence === PolicyPeriodDuration.Yearly
  const isOneTime = programCadence === PolicyPeriodDuration.OneTime

  const [categoryTaxableSettings, setCategoryTaxableSettings] = useState<
    Record<number, CategoryTaxability>
  >(defaultValues?.categoryTaxableSettings || {})

  const { showModal } = useConfirmationModalContext()

  const firstSelectedCountry = useWatch({ control, name: 'countries' })
  const currencySymbol = CurrencySymbol[getCurrency(firstSelectedCountry?.[0] ?? CountryCode.US)]
  const allowanceValue = useWatch({ control, name: 'allowance' })

  useEffect(() => {
    if (!allowanceValue) return
    // Strip out everything before the first space
    const cleanedValue = allowanceValue.replace(/^\S*\s/, '').trim()
    // Add the new currency symbol and set the updated value to the allowance field
    const nextValue = `${currencySymbol} ${cleanedValue}`
    if (allowanceValue !== nextValue) setValue('allowance', nextValue)
  }, [currencySymbol, setValue, allowanceValue])

  const handleSubmission: SubmitHandler<BenefitProgramFormValues> = async (data) => {
    if (!programCadence) {
      toast.error('Please select the policy period duration.')
      return
    }
    if (!benefitProgram && isAnnual && !annualResetMonth) {
      toast.error('Please select the month the annual allowance resets.')
      return
    }
    if (!data.selectedCategories.length) {
      toast.error('Please select a category.')
      return
    }
    if (!data.countries.length) {
      toast.error('Please select a country.')
      return
    }

    const result = await showModal(
      benefitProgram ? 'Update Program' : 'Create Program',
      `Are you sure you want to ${benefitProgram ? 'update' : 'create'} this program?`
    )

    if (!result || !employerId) {
      return
    }

    const benefitProgramCategories = convertToBackendFormat(
      data.selectedCategories,
      data.categoryTaxableSettings
    )

    if (benefitProgram) {
      updateBenefitProgramQuery({
        ...data,
        eligibilityDurationDays: undefined,
        rollover,
        benefitProgramId: benefitProgram.id,
        benefitProgramCategories
      })
    } else {
      createBenefitProgramQuery({
        ...data,
        employerId: employerId,
        allowance: `${parseAmount(data.allowance)}`,
        ongoingPurchases: isAnnual || isOneTime ? false : data.ongoingPurchases,
        policyPeriod: programCadence,
        annualResetMonth: isAnnual ? Number(annualResetMonth) : undefined,
        eligibilityDurationDays:
          isOneTime && oneTimeDuration === OneTimeDuration.Days
            ? Number(data.eligibilityDurationDays)
            : undefined,
        scheduledEndDate: isOneTime ? undefined : data.scheduledEndDate,
        rollover: isOneTime || isAnnual ? RolloverOptions.Disabled : rollover,
        benefitProgramCategories
      })
    }
    closeModal()
  }

  const handleError: SubmitErrorHandler<BenefitProgramFormValues> = (error) => {
    toast.error(
      error.name?.message ??
        error.selectedCategories?.message ??
        error.countries?.message ??
        error.benefitProgramLink?.message ??
        error.scheduledStartDate?.message ??
        error.allowance?.message ??
        error.policyPeriod?.message ??
        error.ongoingPurchaseThreshold?.message ??
        error.eligibilityDurationDays?.message ??
        error.scheduledEndDate?.message
    )
  }

  const handleEndPolicy: SubmitHandler<BenefitProgramFormValues> = async (data) => {
    if (!benefitProgram) return

    const { scheduledEndDate, deleteFuturePolicies } = data

    if (!scheduledEndDate) return

    endBenefitProgramPolicyQuery({
      benefitProgramId: benefitProgram.id,
      endDate: scheduledEndDate,
      deleteFuturePolicies: !!deleteFuturePolicies
    })

    closeModal()
  }

  const handleUpdatePolicy: SubmitHandler<BenefitProgramFormValues> = async (data) => {
    if (!benefitProgram) return

    const { scheduledStartDate, allowance, ongoingPurchases, ongoingPurchaseThreshold } = data

    updateBenefitProgramPolicyQuery({
      benefitProgramId: benefitProgram.id,
      startDate: scheduledStartDate,
      allowance: `${parseAmount(allowance)}`,
      rollover,
      ongoingPurchases,
      ongoingPurchaseThreshold: ongoingPurchaseThreshold || DEFAULT_THRESHOLD
    })

    closeModal()
  }

  const policyPeriodOptions = Object.values(PolicyPeriodDuration).map((value) => ({
    value,
    label: PolicyPeriodDurationLabel.get(value) ?? value
  }))

  const annualResetMonthOptions = getAnnualResetMonthOptions()

  const oneTimeDurationOptions = [
    { value: OneTimeDuration.Indefinite, label: 'Indefinite' },
    { value: OneTimeDuration.Days, label: 'Ends after a number of days' }
  ]

  const rolloverOptions = [
    { value: RolloverOptions.Disabled, label: 'Disabled' },
    { value: RolloverOptions.Indefinite, label: 'Indefinite' },
    { value: RolloverOptions.EndOfYear, label: 'End of Year' }
  ]

  const [rollover, setRollover] = useState(
    benefitProgram ? benefitProgram.policy.rollover : RolloverOptions.Disabled
  )

  const benefitProgramRegisterOptions = {
    name: { required: 'Please enter a name for the program' },
    benefitProgramLink: { required: 'Please provide a category guide link' },
    scheduledStartDate: {
      required: 'Please provide a program start date',
      validate: (selectedDate: Date) => {
        if (!selectedDate || (!isAnnual && !isOneTime)) return true
        if (selectedDate.getDate() !== 1) return 'Start date must be the 1st of a month'
        if (
          isAnnual &&
          annualResetMonth &&
          selectedDate.getMonth() + 1 !== Number(annualResetMonth)
        ) {
          return 'Start date must be in the month the allowance resets'
        }
        return true
      },
      onChange: (selectedDate: Date) => {
        if (!startDateChanged) setStartDateChanged(true)
        return selectedDate
      }
    },
    eligibilityDurationDays: {
      required: 'Please enter how many days the benefit lasts',
      min: { value: 1, message: 'The benefit must last at least one day' }
    },
    ongoingPurchaseThreshold: {
      required: 'Please enter an ongoing purchase thresold'
    },
    allowance: {
      validate: (value: unknown) => isAmount(value) || 'Please enter a valid expenditure allowance.'
    },
    updatePolicyStartDate: {
      validate: (selectedDate: Date) => {
        const errorMessage = 'Please enter a valid policy start date'
        const defaultStartDate = defaultValues.scheduledStartDate

        if (!defaultStartDate) return errorMessage

        // If there is no active policy, start date can be the same default date
        if (defaultStartDate > new Date()) {
          return startDateChanged ? true : errorMessage
        }

        const invalidDate = isSameDay(selectedDate, defaultStartDate)

        return invalidDate ? errorMessage : true
      }
    },
    endPolicyEndDate: {
      validate: (selectedDate?: Date) => {
        const invalidDateMessage = 'Please enter a valid policy end date'
        const unchangedDateMessage = 'Please select a different policy end date'

        if (!selectedDate) return invalidDateMessage

        const { scheduledEndDate } = defaultValues
        const endDateSame = !scheduledEndDate ? false : isSameDay(selectedDate, scheduledEndDate)

        return endDateSame ? unchangedDateMessage : true
      }
    }
  }

  const handleTaxableChange = (categoryId: number, taxable: CategoryTaxability) => {
    setCategoryTaxableSettings((prev) => {
      const next = { ...prev, [categoryId]: taxable }
      setValue('categoryTaxableSettings', next)
      return next
    })
  }

  return {
    register,
    control,
    handleSubmit,
    currencySymbol,
    errors,
    programCadence,
    setProgramCadence,
    rollover,
    setRollover,
    rolloverOptions,
    handleSubmission,
    handleError,
    handleUpdatePolicy,
    handleEndPolicy,
    policyPeriodOptions,
    benefitProgramRegisterOptions,
    categoryTaxableSettings,
    handleTaxableChange,
    isAnnual,
    isOneTime,
    annualResetMonth,
    setAnnualResetMonth,
    annualResetMonthOptions,
    oneTimeDuration,
    setOneTimeDuration,
    oneTimeDurationOptions,
    dateBasis,
    endDateBasis,
    scheduledStartDate
  }
}
